import { useMemo, useState } from "react"
import { DataTable, EmptyState, MoneyText, en, formatDateTime } from "procurement/design"
import { Button } from "../../ui"

const orderLink = (r) => `/procurement/orders/${r.orderRequestId}`

const COLUMNS = [
  {
    key: "name",
    header: "Order / Item",
    className: "pc-col-title",
    render: (r) => (r.depth ? r.itemName : <strong>{r.orCode}</strong>),
  },
  {
    key: "center",
    header: "Center",
    render: (r) => (r.depth ? r.budgetCenterName : r.budgetCenterNames) || "—",
  },
  { key: "categoryName", header: "Category" },
  {
    key: "qty",
    header: "Lines / Qty",
    align: "right",
    render: (r) => (r.depth ? r.quantity ?? "—" : `${r.lineCount ?? r.lines?.length ?? 0} lines`),
  },
  {
    key: "status",
    header: "Status",
    render: (r) => (r.depth ? r.orLineStatus : r.orStatus) || "—",
  },
  {
    key: "orRequestedAmount",
    header: "Requested amount",
    align: "right",
    render: (r) => <MoneyText value={r.orRequestedAmount} />,
  },
  {
    key: "documentEventAtLocal",
    header: "Date",
    className: "pc-col-nowrap",
    render: (r) => (r.depth ? "" : formatDateTime(r.documentEventAtLocal)),
  },
]

// Paged server-side by Order, so sorting a single page would mislead — rows keep BE order.
const OrderDetailTable = ({ data, isFetching, page, size, onPageChange, navigate }) => {
  const [expanded, setExpanded] = useState(() => new Set())
  const orders = useMemo(() => data?.items || [], [data])

  const rows = useMemo(
    () =>
      orders.flatMap((o) => {
        const key = `or-${o.orderRequestId}`
        const parent = { ...o, key, expandable: (o.lines || []).length > 0 }
        if (!expanded.has(key)) return [parent]
        return [parent, ...o.lines.map((l) => ({ ...l, key: l.recordKey || `orl-${l.orLineId}`, depth: 1 }))]
      }),
    [orders, expanded]
  )

  const columns = useMemo(
    () => [
      ...COLUMNS,
      {
        key: "open",
        header: "",
        render: (r) =>
          r.depth ? null : (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                navigate(orderLink(r))
              }}
            >
              Open
            </Button>
          ),
      },
    ],
    [navigate]
  )

  const toggle = (row) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(row.key)) next.delete(row.key)
      else next.add(row.key)
      return next
    })

  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / size))

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isFetching}
        density="compact"
        sortRows={false}
        hidePagination
        expandedKeys={expanded}
        onToggleExpand={toggle}
        onRowClick={(r) => r.orderRequestId && navigate(orderLink(r))}
        empty={<EmptyState title="No orders for these filters." />}
      />
      {total > 0 && (
        <div className="ds-table-pager">
          <span>{en.common.results(total)}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Button size="sm" disabled={page <= 0 || isFetching} onClick={() => onPageChange(page - 1)}>
              Prev
            </Button>
            <span>{en.common.pageOf(page + 1, pages)}</span>
            <Button size="sm" disabled={page >= pages - 1 || isFetching} onClick={() => onPageChange(page + 1)}>
              Next
            </Button>
          </span>
        </div>
      )}
    </>
  )
}

export default OrderDetailTable
